import {
  ActivityStatus,
  ActivityType,
  type SaveGhccActivityProgressData,
  type SaveNonGhccActivityProgressData,
  type Activity,
  type ActivityProgressUpdate,
} from '@/models/Activity';
import { ActivityMode, ContentType } from '@/models/Session';
import { type Lesson } from '@/models/Lesson';
import { type Course } from '@/models/Course';
import { GhccRequestSuffix } from '@/models/Ghcc';

import { ref, watch } from 'vue';
import { defineStore, storeToRefs } from 'pinia';

import { ACTIVITY_PROGRESS_SAVE_INTERVAL, ACTIVITY_PROGRESS_SAVE_TIMEOUT } from '@/utils/constants';
import { getActivityPosition } from '@/utils/course';
import { getActivityStatus, getTestStatus, saveActivityProgress } from '@/utils/activity';
import { isFirefox } from '@/utils/browser';

import { useSessionStore } from './useSessionStore';
import { useContentStore } from './useContentStore';
import { useCourseStore } from './useCourseStore';
import { useLessonStore } from './useLessonStore';

export const useActivityProgressStore = defineStore('activityProgress', () => {
  let increaseSessionTimeInterval: NodeJS.Timeout | undefined;
  let savingProgressInterval: NodeJS.Timeout | undefined;

  const sessionTimeInSeconds = ref<number>(0);
  const lastActivityModule = ref<string>();
  const lastActivity = ref<string>();
  const progress = ref<ActivityProgressUpdate>();
  const autoSaveProgress = ref<boolean>(isFirefox());

  const { contentType, activityMode, lmsSessionId } = storeToRefs(useSessionStore());
  const { contentData } = storeToRefs(useContentStore());
  const { handleRemoteScormUpdateScore, setCourseSummary } = useCourseStore();
  const { setLessonStatus } = useLessonStore();

  const setProgress = (newProgress: ActivityProgressUpdate) => {
    progress.value = newProgress;
  };

  const findTargetActivity = (courseCode: string) => {
    let targetModule: string | undefined;
    let targetActivity: Activity | undefined;

    if (contentType.value === ContentType.course) {
      const content = contentData.value as Course;

      const { moduleIndex, activityIndex } = getActivityPosition(content, courseCode);

      if (moduleIndex > -1) {
        targetModule = content.modules[moduleIndex].id.toString();
        targetActivity = content.modules[moduleIndex].activities[activityIndex];
      } else {
        const quickscan = content.quickscan?.activities.find(
          (activity) => activity.courseCode.toLowerCase() === courseCode.toLowerCase(),
        );

        if (quickscan) {
          targetModule = content.modules[0].id.toString();
          targetActivity = quickscan;
        } else {
          const test = content.certification?.activities.find(
            (activity) => activity.courseCode.toLowerCase() === courseCode.toLowerCase(),
          );
          if (test) {
            targetModule = 'certification';
            targetActivity = test;
          }
        }
      }
    } else if (contentType.value === ContentType.lesson) {
      targetModule = 'lesson';
      targetActivity = (contentData.value as Lesson).steps
        .map((step) => step.activity)
        .find((activity) => activity.courseCode?.toLowerCase() === courseCode.toLowerCase());
    }

    return {
      targetModule,
      targetActivity,
    };
  };

  const updateUI = (
    targetActivity: Activity,
    score: number,
    sessionTime: number,
    status: ActivityStatus,
  ) => {
    const isAlreadyCompleted = targetActivity.status === ActivityStatus.completed;
    const shouldTrack = !isAlreadyCompleted && status === ActivityStatus.completed;

    if (!isAlreadyCompleted) {
      targetActivity.progress = score;
      targetActivity.status = status;
    }

    if (targetActivity.studiedDuration) targetActivity.studiedDuration += sessionTime;

    if (contentType.value === ContentType.course) {
      setCourseSummary(targetActivity);
    } else if (contentType.value === ContentType.lesson) {
      setLessonStatus(shouldTrack);
    }
  };

  const saveProgress = async () => {
    if (!progress.value || sessionTimeInSeconds.value <= 0) return;

    const nowTimespan = Math.floor(Date.now() / 1000);

    const sessionTime = sessionTimeInSeconds.value;
    sessionTimeInSeconds.value = 0;

    const startedTimespan = nowTimespan - sessionTime;

    const { targetModule, targetActivity } = findTargetActivity(progress.value.courseCode);

    if (!targetActivity) return;

    const isTestActivity = targetActivity.nodeTypeId === 40055;

    const score = isTestActivity
      ? progress.value.score ?? 0
      : progress.value.completionPercent > (targetActivity.progress as number)
        ? progress.value.completionPercent
        : (targetActivity.progress as number);

    const status = isTestActivity ? getTestStatus(progress.value.score) : getActivityStatus(score);

    const isGhcc =
      [undefined, ActivityType.ghccActivity].includes(targetActivity.activityType) ||
      !targetActivity.activityUrl;
    const activityId = isGhcc ? progress.value.courseCode : targetActivity.id.toString();

    const commonParams: {
      ModuleStart: boolean;
      PlayerStart: boolean;
      contextId?: string;
      lmsConnectorSessionId?: string;
    } = {
      ModuleStart: lastActivityModule.value !== targetModule,
      PlayerStart: lastActivity.value !== targetActivity.courseCode,
      contextId:
        contentType.value === ContentType.lesson || activityMode.value === ActivityMode.normal
          ? contentData.value?.code
          : targetActivity.courseCode,
    };

    if (lmsSessionId.value) {
      commonParams.lmsConnectorSessionId = lmsSessionId.value;
    }

    const params: SaveGhccActivityProgressData | SaveNonGhccActivityProgressData = isGhcc
      ? {
          suffix: GhccRequestSuffix.GetResults,
          moduleId: activityId,
          ts1: startedTimespan,
          ts2: nowTimespan,
          ...commonParams,
        }
      : {
          courseCode: progress.value.courseCode,
          score,
          activityId,
          sessionTime,
          ...commonParams,
        };

    await saveActivityProgress(params);

    lastActivityModule.value = targetModule;
    lastActivity.value = targetActivity.courseCode;

    updateUI(targetActivity, score, sessionTime, status);
  };

  const startSavingProgress = () => {
    if (!autoSaveProgress.value) return;

    saveProgress();

    savingProgressInterval = setInterval(saveProgress, ACTIVITY_PROGRESS_SAVE_INTERVAL);
  };

  const increaseSessionTime = () => {
    if (document.visibilityState === 'hidden') return;

    sessionTimeInSeconds.value++;
  };

  const startSessionTime = () => {
    window.addEventListener('beforeunload', onBeforeUnload);

    increaseSessionTimeInterval = setInterval(increaseSessionTime, 1000);

    setTimeout(startSavingProgress, ACTIVITY_PROGRESS_SAVE_TIMEOUT);
  };

  const resetSessionTime = () => {
    window.removeEventListener('beforeunload', onBeforeUnload);

    sessionTimeInSeconds.value = 0;

    if (increaseSessionTimeInterval) {
      clearInterval(increaseSessionTimeInterval);
      increaseSessionTimeInterval = undefined;
    }

    if (savingProgressInterval) {
      clearInterval(savingProgressInterval);
      savingProgressInterval = undefined;
    }

    progress.value = undefined;
  };

  const stopSessionTime = async () => {
    await saveProgress();
    resetSessionTime();
  };

  const onBeforeUnload = async () => {
    if (!autoSaveProgress.value) {
      await saveProgress();
    }
  };

  watch(
    progress,
    (newProgress) => {
      if (newProgress && activityMode.value === ActivityMode.single) {
        handleRemoteScormUpdateScore(newProgress.completionPercent, undefined);
      }
    },
    { immediate: true, deep: true },
  );

  return {
    setProgress,
    saveProgress,
    startSessionTime,
    stopSessionTime,
  };
});
